import React, { useState, useEffect } from 'react';
import {
    Box,
    VStack,
    HStack,
    Text,
    Heading,
    FormControl,
    FormLabel,
    Input,
    Button,
    Divider,
    Alert,
    AlertTitle,
    AlertDescription,
    ListItem,
    UnorderedList,
    Link,
    useToast,
    Icon,
    Flex,
    Badge,
    Card,
    Tag,
    Center,
    Spinner
} from '@chakra-ui/react';
import {
    FiSave,
    FiDownload,
    FiExternalLink,
    FiCheckCircle,
    FiInfo
} from 'react-icons/fi';

import Header from '../../components/Header';
import API from '../../helpers/axiosInstance';
import { fetchWrapper } from '../../helpers';
import { useLocation, useNavigate } from 'react-router-dom';
import { LuArrowLeft } from 'react-icons/lu';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
import usePagePermissions from '../../hooks/usePagePermissions';

const APNConfigurationPage = ({ header, embedded, group }) => {
    const [formData, setFormData] = useState({
        teamId: '',
        keyId: '',
        bundleId: '',
        p8Key: null
    });
    const [hasExistingFile, setHasExistingFile] = useState(false);
    const [loading, setLoading] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();
    const locationState = useLocation().state;

    const { loading: permissionsLoading, can } = usePagePermissions(group || 'documentation/configure-apn');

    const configureApnDocPermission = can("configure-apn-doc");
    // Page always renders (guide content is always useful); only the mutating
    // Save action is gated behind the permission.
    const canSaveApnConfig = configureApnDocPermission;

    // The banner/form must reflect the real status regardless of how this page was
    // reached — ConfigBanner passes iosConfiguration + isConfigured via location.state,
    // but a direct sidebar link (or a refresh) has none, and iosConfiguration used to
    // be read unguarded below (a crash waiting to happen on direct navigation). Fetch
    // the same /get-sdk-key data ConfigBanner itself uses, preferring that once it's
    // in; location.state covers the gap for that first render when it's present.
    const [sdkKeyData, setSdkKeyData] = useState(null);
    const iosConfiguration = sdkKeyData ? sdkKeyData?.ios_config : locationState?.iosConfiguration;
    const isConfigured = sdkKeyData ? !!sdkKeyData?.ios : locationState?.isConfigured;

    useEffect(() => {
        const loadSdkKey = async () => {
            try {
                const res = await fetchWrapper.get(`${process.env.REACT_APP_API_URL}/customer/get-sdk-key`);
                setSdkKeyData(res || {});
            } catch (error) {
                console.error('Error fetching SDK key status:', error);
            }
        };
        loadSdkKey();
    }, []);

    const isDisabled = (
        formData.teamId === iosConfiguration?.team_id &&
        formData.keyId === iosConfiguration?.key_id &&
        formData.bundleId === iosConfiguration?.bundle_id &&
        formData.p8Key === null // no file selected, so unchanged
    );

    // Set data from props on mount or when iosConfiguration changes
    useEffect(() => {
        if (iosConfiguration) {
            setFormData(prev => ({
                ...prev,
                teamId: iosConfiguration?.team_id || '',
                keyId: iosConfiguration?.key_id || '',
                bundleId: iosConfiguration?.bundle_id || ''
            }));

            // If any field is filled, assume file was uploaded before
            if (iosConfiguration?.team_id || iosConfiguration?.key_id || iosConfiguration?.bundle_id) {
                setHasExistingFile(true);
            }
        }
    }, [iosConfiguration]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file && file.name.endsWith('.p8')) {
            setFormData(prev => ({
                ...prev,
                p8Key: file
            }));
            toast({
                title: 'File uploaded successfully',
                description: `${file.name} has been selected`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } else {
            toast({
                title: 'Invalid file type',
                description: 'Please upload a .p8 file',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleSaveSettings = async () => {
        if (!formData.teamId || !formData.keyId || !formData.bundleId) {
            toast({
                title: 'Missing information',
                description: 'Please fill in all required fields',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        // If there's no existing file and no new file selected
        if (!hasExistingFile && !formData.p8Key) {
            toast({
                title: 'Missing .p8 file',
                description: 'Please upload the .p8 key file',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setLoading(true);

        // Prepare FormData
        const data = new FormData();
        data.append('team_id', formData.teamId);
        data.append('key_id', formData.keyId);
        data.append('bundle_id', formData.bundleId);

        // Only append file if a new one was selected
        if (formData.p8Key) {
            data.append('p8Key', formData.p8Key);
        }

        try {
            const response = await API.post(
                `${process.env.REACT_APP_API_URL_XPUSH}/upload-apn`,
                data,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        "x-api-key": localStorage.getItem('api-key'),
                    }
                }
            );

            // Success: show toast
            toast({
                title: 'Settings saved successfully',
                description: 'APN configuration has been updated',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            // Update hasExistingFile if we just uploaded a new file
            if (formData.p8Key) {
                setHasExistingFile(true);
                setFormData(prev => ({ ...prev, p8Key: null }));
            }
        } catch (error) {
            let description = error.message;
            if (error.response && error.response.data) {
                description = error.response.data.message || JSON.stringify(error.response.data);
            }
            toast({
                title: 'Error',
                description,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    if (permissionsLoading) {
        if (embedded) {
            return (
                <Center h="200px">
                    <Spinner size="xl" color="blue.500" thickness="4px" />
                </Center>
            );
        }
        return (
            <>
                <Header header={header} />
                <BoxWrapper>
                    <Center h="400px">
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                    </Center>
                </BoxWrapper>
            </>
        );
    }

    const content = (
                        <VStack spacing={8} align="stretch">
                            {/* Same banner ConfigBanner shows inline in the SDK docs — repeated
                                here since this page is also reachable directly (sidebar quick
                                link), where there's no location.state to fall back on. */}
                            <Box
                                borderRadius="8.75px"
                                border="1px solid"
                                borderColor={isConfigured ? '#00c951' : '#ff8904'}
                                bg={isConfigured ? '#f0fdf4' : '#fff7ed'}
                                p={4}
                            >
                                <HStack spacing={3} align="center">
                                    <Icon
                                        as={isConfigured ? FiCheckCircle : FiInfo}
                                        color={isConfigured ? 'green.500' : 'orange.500'}
                                        boxSize={5}
                                    />
                                    <Box>
                                        <Text fontSize="md" fontWeight="semibold" color={isConfigured ? 'green.800' : 'orange.800'} mb={1}>
                                            Push Notification {isConfigured ? 'Configured' : 'Not Configured'}
                                        </Text>
                                        <Text fontSize="sm" color={isConfigured ? 'green.700' : 'orange.700'}>
                                            {isConfigured
                                                ? 'Your push notification service is successfully configured and ready to use.'
                                                : 'Configure your push notification service to enable real-time notifications.'}
                                        </Text>
                                    </Box>
                                </HStack>
                            </Box>

                            {/* Header */}
                            <Box>
                                {/* Introduction */}
                                <Box>
                                    <Heading size="md" color="gray.700" mb={4}>Introduction</Heading>
                                    <Text color="gray.600" lineHeight="1.6" mb={4}>
                                        We utilize Apple Push Notification Service (APNs) to efficiently wake our application and establish a secure connection with our Push Notification Server. Once the
                                        connection is active, all push notifications are delivered directly from our server, giving us full control over message content and delivery flow.
                                    </Text>
                                    <Text color="gray.600" lineHeight="1.6">
                                        By leveraging APNs deep integration with iOS at the system level, we ensure optimal battery performance while maintaining real-time connectivity. This design
                                        minimizes unnecessary background activity and guarantees reliable notification delivery without compromising device efficiency. Our approach is built to offer a
                                        seamless and responsive push notification experience, prioritizing both user privacy and battery conservation.
                                    </Text>
                                </Box>
                            </Box>

                            <Divider />

                            {/* Configuration Form */}
                            <Box width="100%">
                                <Heading size="md" color="gray.700" mb={6}>Configure APN Settings</Heading>
                                <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                                    <HStack spacing={6} align="stretch">
                                        <FormControl>
                                            <FormLabel fontWeight={"semibold"} fontSize={"sm"}>
                                                Team ID
                                            </FormLabel>
                                            <Input
                                                placeholder="Apple Team ID"
                                                value={formData.teamId}
                                                onChange={(e) => handleInputChange('teamId', e.target.value)}
                                                bg="gray.50"
                                                border="1px"
                                                borderColor="gray.200"
                                                _focus={{ borderColor: 'var(--brand-primary)', bg: 'white' }}
                                            />
                                        </FormControl>

                                        <FormControl>
                                            <FormLabel fontWeight={"semibold"} fontSize={"sm"}>
                                                Key ID
                                            </FormLabel>
                                            <Input
                                                placeholder="Apple Key ID"
                                                value={formData.keyId}
                                                onChange={(e) => handleInputChange('keyId', e.target.value)}
                                                bg="gray.50"
                                                border="1px"
                                                borderColor="gray.200"
                                                _focus={{ borderColor: 'var(--brand-primary)', bg: 'white' }}
                                            />
                                        </FormControl>

                                        <FormControl>
                                            <FormLabel fontWeight={"semibold"} fontSize={"sm"}>
                                                Bundle ID
                                            </FormLabel>
                                            <Input
                                                placeholder="Apple Bundle ID"
                                                value={formData.bundleId}
                                                onChange={(e) => handleInputChange('bundleId', e.target.value)}
                                                bg="gray.50"
                                                border="1px"
                                                borderColor="gray.200"
                                                _focus={{ borderColor: 'var(--brand-primary)', bg: 'white' }}
                                            // isDisabled={fetchingConfig}
                                            />
                                        </FormControl>


                                    </HStack>
                                    <FormControl my={3}>
                                        <FormLabel fontWeight={"semibold"} fontSize={"sm"}>
                                            Upload .p8 File
                                        </FormLabel>
                                        <Box>
                                            <input
                                                type="file"
                                                accept=".p8"
                                                onChange={handleFileUpload}
                                                style={{ display: 'none' }}
                                                id="p8-file-upload"
                                            />
                                            <Button
                                                as="label"
                                                htmlFor="p8-file-upload"
                                                color={"black"}
                                                variant="outline"
                                                colorScheme="whiteAlpha"
                                                cursor="pointer"
                                                size={"sm"}
                                                bg="gray.50"
                                                border="1px"
                                                borderColor="gray.200"

                                            // isDisabled={fetchingConfig}
                                            >
                                                {formData.p8Key ? formData.p8Key.name : 'Choose File'}
                                            </Button>

                                            {/* Show status based on existing file or new file */}
                                            {formData.p8Key ? (
                                                <Badge colorScheme="green" mt={2} ml={3}>
                                                    New file selected: {formData.p8Key.name}
                                                </Badge>
                                            ) : hasExistingFile ? (
                                                <Flex align="center" mt={2} >
                                                    <Icon as={FiCheckCircle} color="green.500" mr={2} />
                                                    <Text fontSize="sm" color="green.600" fontWeight="medium">
                                                        Your .p8 file is uploaded. Choose a new file to override.
                                                    </Text>
                                                </Flex>
                                            ) : (
                                                <Text fontSize="sm" color="gray.500" mt={2} ml={3}>
                                                    No file selected
                                                </Text>
                                            )}
                                        </Box>
                                    </FormControl>

                                    {canSaveApnConfig ? (
                                        <Button
                                            leftIcon={<Icon as={FiSave} />}
                                            colorScheme="blue"
                                            size="sm"
                                            onClick={handleSaveSettings}
                                            isLoading={loading}
                                            loadingText="Saving Settings..."
                                            alignSelf="flex-start"
                                            disabled={isDisabled}
                                        >
                                            Save Settings
                                        </Button>
                                    ) : (
                                        <Tag size="sm" variant="outline" colorScheme="gray" alignSelf="flex-start">View only</Tag>
                                    )}
                                </Card>
                            </Box>

                            <Divider />

                            {/* Guide Section */}
                            <Box>
                                <Heading size="md" color="gray.700" mb={6}>
                                    Guide: How to Retrieve Apple Developer Credentials for APNs Integration
                                </Heading>

                                <VStack spacing={6} align="stretch">
                                    <Alert bg={"var(--brand-tint)"} borderRadius="md" border={"1px solid var(--brand-tint-strong)"} boxShadow={"0 1px 3px 0 #0000001a"}>
                                        <Box>
                                            <AlertTitle color={"var(--brand-900)"}>Requirements</AlertTitle>
                                            <AlertDescription color={"var(--brand-900)"} fontSize={"xs"}>
                                                To enable Push Notification functionality for iOS device registration,
                                                certificates and Apple Developer account must be generated and securely integrated
                                                into our system. This guide provides a step-by-step process to retrieve your{' '}
                                                <strong>Team ID</strong>, <strong>Key ID</strong>, and <strong>Bundle ID</strong>,{' '}
                                                as well as download and upload the required <strong>.p8 APNs authentication key</strong>{' '}
                                                for seamless push notification delivery through Apple Push Notification Service (APNs).
                                            </AlertDescription>
                                        </Box>
                                    </Alert>

                                    {/* Step 1 */}
                                    <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                                        <Heading size="sm" mb={4} color="#364153">
                                            <HStack>
                                                <Text fontSize={"lg"} fontWeight={"semibold"} color={"black"}>Step 1: Get Team ID</Text>
                                            </HStack>
                                        </Heading>
                                        <UnorderedList spacing={2} pl={4}>
                                            <ListItem>
                                                Log in to{' '}
                                                <Link
                                                    href="https://developer.apple.com"
                                                    color="var(--brand-primary)"
                                                    isExternal
                                                    textDecoration="underline"
                                                >
                                                    Apple Developer Portal <Icon as={FiExternalLink} />
                                                </Link>
                                            </ListItem>
                                            <ListItem>In the top-right corner, click your name / account name.</ListItem>
                                            <ListItem>Select Membership from the drop-down.</ListItem>
                                            <ListItem>You'll see your Team ID listed under the Team Information section.</ListItem>
                                        </UnorderedList>
                                    </Card>

                                    {/* Step 2 */}
                                    <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"}>
                                        <Heading size="sm" mb={4} color="#364153">
                                            <HStack>
                                                <Text fontSize={"lg"} fontWeight={"semibold"} color={"black"}>Step 2: Get Key ID and Download .p8 Key</Text>
                                            </HStack>
                                        </Heading>
                                        <UnorderedList spacing={2} pl={4}>
                                            <ListItem>
                                                Go to the{' '}
                                                <Link
                                                    href="https://developer.apple.com/account/resources/authkeys/list"
                                                    color="var(--brand-primary)"
                                                    isExternal
                                                    textDecoration="underline"
                                                >
                                                    Apple Developer Certificates, Identifiers & Profiles page <Icon as={FiExternalLink} />
                                                </Link>
                                            </ListItem>
                                            <ListItem>Click the "+" button to create a new key.</ListItem>
                                            <ListItem>Enter a name (e.g., Push Notification Key) and check the box for Apple Push Notifications service (APNs).</ListItem>
                                            <ListItem>Click Continue → Register.</ListItem>
                                            <ListItem>After registering, you'll see:</ListItem>
                                            <UnorderedList pl={6} spacing={1}>
                                                <ListItem><strong>Key ID</strong> — note this down.</ListItem>
                                                <ListItem>
                                                    <Flex alignItems="center">
                                                        <Text>A Download button — click it to download the .p8 file (only available once).</Text>
                                                        <Icon as={FiDownload} ml={2} color="orange.500" />
                                                    </Flex>
                                                </ListItem>
                                            </UnorderedList>
                                            <ListItem>Save the .p8 file securely (e.g., AuthKey_ABC123XYZ.p8).</ListItem>
                                        </UnorderedList>
                                    </Card>

                                    {/* Step 3 */}
                                    <Card p={3} boxShadow={"0 1px 3px 0 #0000001a"} border={"1px solid #0000001a"} mb={4}>
                                        <Heading size="sm" mb={4} color="#364153">
                                            <HStack>
                                                <Badge colorScheme="blue" variant="solid"></Badge>
                                                <Text fontSize={"lg"} fontWeight={"semibold"} color={"black"}>Step 3: Find Bundle ID</Text>
                                            </HStack>
                                        </Heading>
                                        <UnorderedList spacing={2} pl={4}>
                                            <ListItem>
                                                Go to the{' '}
                                                <Link
                                                    href="https://developer.apple.com/account/resources/identifiers/list"
                                                    color="var(--brand-primary)"
                                                    isExternal
                                                    textDecoration="underline"
                                                >
                                                    identifiers section <Icon as={FiExternalLink} />
                                                </Link>{' '}
                                                of your developer account.
                                            </ListItem>
                                            <ListItem>Select your App ID from the list (or create a new one if needed).</ListItem>
                                            <ListItem>Your Bundle ID is listed there, e.g., com.companyname.appname.</ListItem>
                                        </UnorderedList>
                                    </Card>
                                </VStack>
                            </Box>
                        </VStack>
    );

    if (embedded) {
        return (
            <Box>
                <Flex justify="space-between" alignItems="center">
                    <Text fontSize="2xl" fontWeight="semibold">
                        Configure Apple Push Notification (APN)
                    </Text>
                    <Tag bg={"#ff6b35"} color={"white"} fontSize={"xs"}>
                        Swift
                    </Tag>
                </Flex>
                <Divider my={2} />
                {content}
            </Box>
        );
    }

    return (
        <>
            <Header header={header} />
            <BoxWrapper>
                <Flex justify={"center"} align={"center"}>
                    <HStack w={"70%"} mt={8}>
                        <Button
                            onClick={handleBack}
                            as="label"
                            leftIcon={<Icon as={LuArrowLeft} />}
                            color={"black"}
                            variant="outline"
                            colorScheme="whiteAlpha"
                            cursor="pointer"
                            size={"sm"}
                            bg="gray.50"
                            border="1px"
                            borderColor="gray.200"
                            sx={{
                                "&:hover": {
                                    background: "#3131311a !important",
                                    color: "black !important",

                                },
                            }}
                        >
                            Back
                        </Button>
                        <Text fontSize="32px" fontWeight="300" color="#000000" my="0px" >
                            Configure Apple Push Notification (APN)
                        </Text>
                        <Tag bg={"#ff6b35"} color={"white"} fontSize={"xs"}>
                            Swift
                        </Tag>
                    </HStack>
                </Flex>
                <Divider bg={"#eaeaea"} my={8} />
                <Flex justify={"center"} align="center">
                    <Box w={"70%"}>
                        {content}
                    </Box>
                </Flex>

            </BoxWrapper>
        </>
    );
};

export default APNConfigurationPage;